import React from 'react';
import {
    Box,
    IconButton,
    Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { AiChatPage } from '../../contractsPage/AiChatPage';
import { ModelSwitcher } from '../../../common/ModelSwitcher';
import { useModelSwitcher } from '../../../../hooks/useModelSwitcher';
import { selectIsSuperAdmin, selectIsUniversalAdmin } from '../../../store/selectors/session';
import { RootState } from '../../../store/reducers';

const useStyles = makeStyles((theme: any) => ({
    // Base container styles
    chatContainer: {
        backgroundColor: '#ffffff',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        position: 'relative',
        height: '100%', // Ensure it takes full height from parent
        minHeight: 0, // Allow flex shrinking
    },
    chatBody: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    // Action button styles - simple outlined style matching design
    actionButton: {
        width: '32px',
        height: '32px',
        minWidth: '32px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        color: '#666',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#999',
        },
    },
}));

interface AIChatSectionProps {
    open: boolean;
    onClose?: () => void;
    claimAnalysisId?: string | number;
    claimNumber?: string;
    selectedClaimIds?: number[];
    totalClaimsCount?: number;
    externalMessage?: string;
    onMessageSent?: () => void;
    onFiltersUpdate?: (filtersNL: string, filters: any, top_k?: number, sort?: any[], significant_variance?: boolean) => void;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    onClearAllFilters?: () => void;
    preAiModeFilters?: any;
    significanceFlag?: boolean;
}

const AIChatSection: React.FC<AIChatSectionProps> = ({
    open,
    onClose,
    claimAnalysisId,
    claimNumber,
    selectedClaimIds = [],
    totalClaimsCount = 0,
    externalMessage = '',
    onMessageSent,
    onFiltersUpdate,
    isExpanded = false,
    onExpandToggle,
    onClearAllFilters,
    preAiModeFilters,
    significanceFlag = false,
}) => {
    const classes = useStyles();

    // ── Permission gate ───────────────────────────────────────────────────────
    const isSuperAdmin = useSelector<RootState>(selectIsSuperAdmin) as boolean;
    const isUniversalAdmin = useSelector<RootState>(selectIsUniversalAdmin) as boolean;

    // ── Per-session, per-chatbot model overrides ──────────────────────────────
    // Uses 'claim_analysis' key → completely independent from contract/claim sidebars
    const {
        reasoningModel,
        quickModel,
        selectModel,
        isOverridden,
        availableModels,
        isPending: isModelsPending
    } = useModelSwitcher('claim_analysis', isUniversalAdmin);

    // Toggle sidebar minimize state when chat opens/closes
    React.useEffect(() => {
        if (open) {
            if (!document.body.classList.contains('kt-aside--minimize')) {
                document.body.classList.add('kt-aside--minimize');
            }
        }
    }, [open]);

    if (!open) return null;

    // ── Header Actions ────────────────────────────────────────────────────────
    // ── Header Actions (icon buttons only) ──────────────────────────────────
    const headerActions = (
        <React.Fragment>
            {isExpanded ? (
                <Tooltip title="Minimize">
                    <IconButton size="small" className={classes.actionButton} onClick={onExpandToggle}>
                        <CloseFullscreenIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Expand">
                    <IconButton size="small" className={classes.actionButton} onClick={onExpandToggle}>
                        <CropSquareIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}

            {onClose && (
                <Tooltip title="Close">
                    <IconButton size="small" className={classes.actionButton} onClick={onClose}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
        </React.Fragment>
    );

    // ── Model Switcher Slot (Full-width row) ──────────────────────────────────
    const modelSwitcherSlot = isUniversalAdmin ? (
        <ModelSwitcher
            reasoningModel={reasoningModel}
            quickModel={quickModel}
            availableModels={availableModels}
            onModelChange={selectModel}
            isOverridden={isOverridden}
            isDualMode={false}
        />
    ) : null;

    return (
        <Box
            className={`${classes.chatContainer} chat-container-ai-section`}
            sx={{
                width: isExpanded ? '100%' : '400px',
                minWidth: isExpanded ? '100%' : '400px',
                maxWidth: isExpanded ? '100%' : '400px',
                marginLeft: isExpanded ? 0 : '16px',
                marginBottom: '20px', // Match Portlet's kt-portlet class margin-bottom
                height: 'auto !important', // Override the 100% height from chatContainer class
            }}
        >
            {/* Chat Body - AiChatPage with original header */}
            <Box className={classes.chatBody}>
                <AiChatPage
                    selected={[]}
                    height="100%"
                    defaultTitle="Claims Search"
                    isEmbedded={true}
                    chatType="claim_analysis"
                    claimAnalysisId={claimAnalysisId}
                    claimNumber={claimNumber}
                    selectedClaimIds={selectedClaimIds}
                    totalClaimsCount={totalClaimsCount}
                    headerActions={headerActions}
                    externalMessage={externalMessage}
                    onMessageSent={onMessageSent}
                    onFiltersUpdate={onFiltersUpdate}
                    isSidebarMode={!isExpanded}
                    newChatbutton={{
                        enable: true,
                        text: 'Clear all filters',
                    }}
                    enableResponseFilters={false}
                    enableIconTitle={true}
                    onClearAllFilters={onClearAllFilters}
                    preAiModeFilters={preAiModeFilters}
                    enableFeedback={false}
                    significanceFlag={significanceFlag}
                    // ── Model overrides (SuperAdmin only; defaults used otherwise) ──
                    reasoningModel={reasoningModel}
                    quickModel={quickModel}
                    availableModels={availableModels}
                    modelSwitcherSlot={modelSwitcherSlot}
                />
            </Box>
        </Box>
    );
};

export default AIChatSection;
